import { type AppState, type Route, type WikiTab, initialState } from "./state";
import { renderApp, searchResults } from "./render";
import {
  fetchState,
  saveChallenge,
  saveNoteApi,
  streamChat,
  clearChatApi,
  deleteAccountApi,
  fetchReflections,
  saveReflectionApi,
  fetchDiagnostics,
  saveDiagnosticApi,
  fetchSuggestions,
  createSuggestionApi,
  voteSuggestionApi,
  setSuggestionStatusApi,
} from "./api";
import { FRAMEWORKS, FRAMEWORK_ORDER, SESSIONS } from "../shared/content";
import { DIAGNOSTICS, scoreDiagnostic } from "../shared/diagnostics";
import { LABS } from "../shared/labs";

export class App {
  private state: AppState = initialState();
  private root: HTMLElement;
  private draft = "";
  private renderQueued = false;
  private reflTimers: Record<string, number> = {};

  constructor(root: HTMLElement) {
    this.root = root;
  }

  // -------------------------------------------------------------- boot
  async start(): Promise<void> {
    this.attachListeners();
    this.applyPath(location.pathname);
    try {
      this.state.companionWide = localStorage.getItem("hive_companion_wide") === "1";
    } catch {
      /* localStorage unavailable */
    }
    // On phones the Companion is a full-screen overlay, so don't open it by
    // default — the user taps the orb to bring it up.
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 860px)").matches) {
      this.state.companionOpen = false;
    }
    try {
      const data = await fetchState();
      if (!data) {
        this.set({ authed: false, loading: false, authError: this.readAuthError() });
        return;
      }
      const messages = data.messages.map((m) => ({
        id: m.id,
        role: m.role,
        shown: m.content,
        full: m.content,
        diagram: m.diagram,
        cites: m.cites || [],
        done: m.role === "assistant",
      }));
      const challenge = data.user.challenge || "";
      const hasHistory = messages.length > 0 || challenge.trim().length > 0;
      this.set({
        authed: true,
        loading: false,
        user: data.user,
        challenge,
        messages,
        viewState: hasHistory ? "returning" : "firstRun",
      });
      this.onRouteEntered();
    } catch {
      this.set({ authed: false, loading: false, authError: "token" });
    }
  }

  private readAuthError(): string | null {
    const p = new URLSearchParams(location.search);
    return p.get("auth_error");
  }

  // -------------------------------------------------------------- state / render
  private set(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    this.scheduleRender();
  }

  private scheduleRender(): void {
    if (this.renderQueued) return;
    this.renderQueued = true;
    requestAnimationFrame(() => {
      this.renderQueued = false;
      this.render();
    });
  }

  private render(): void {
    this.root.innerHTML = renderApp(this.state);
    this.afterRender();
  }

  private afterRender(): void {
    // Restore the (uncontrolled) composer text + send-button state.
    const composer = document.getElementById("composer") as HTMLTextAreaElement | null;
    if (composer) {
      composer.value = this.draft;
      this.autosize(composer);
      this.updateSendButton();
    }
    if (this.state.searchOpen) {
      const input = document.getElementById("search-input") as HTMLInputElement | null;
      if (input && document.activeElement !== input) {
        input.focus();
        const v = input.value;
        input.value = "";
        input.value = v; // caret to end
      }
    }
    if (this.state.editingChallenge) {
      const ci = document.getElementById("challenge-input") as HTMLTextAreaElement | null;
      ci?.focus();
    }
    this.scrollChat();
  }

  private scrollChat(): void {
    const chat = this.root.querySelector("[data-chat]") as HTMLElement | null;
    if (chat) chat.scrollTop = chat.scrollHeight;
  }

  private scrollCanvasTop(): void {
    const c = this.root.querySelector("[data-canvas]") as HTMLElement | null;
    if (c) c.scrollTop = 0;
  }

  private autosize(el: HTMLTextAreaElement): void {
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  }

  private updateSendButton(): void {
    const btn = document.getElementById("send-btn") as HTMLButtonElement | null;
    if (!btn) return;
    const can = this.draft.trim().length > 0 && !this.state.sending;
    btn.style.background = can ? "#0041ED" : "#C2CEDD";
    btn.style.cursor = can ? "pointer" : "default";
  }

  // -------------------------------------------------------------- listeners
  private attachListeners(): void {
    this.root.addEventListener("click", (e) => this.onClick(e));
    this.root.addEventListener("input", (e) => this.onInput(e));
    this.root.addEventListener("keydown", (e) => this.onKeyDown(e as KeyboardEvent));
    window.addEventListener("keydown", (e) => this.onGlobalKey(e));
    window.addEventListener("popstate", () => {
      this.applyPath(location.pathname);
      this.scheduleRender();
      this.onRouteEntered();
    });
  }

  private onInput(e: Event): void {
    const t = e.target as HTMLElement;
    const refl = t.getAttribute && t.getAttribute("data-refl");
    if (t.id === "composer") {
      this.draft = (t as HTMLTextAreaElement).value;
      this.autosize(t as HTMLTextAreaElement);
      this.updateSendButton();
    } else if (t.id === "search-input") {
      this.state.query = (t as HTMLInputElement).value;
      const box = document.getElementById("search-results");
      if (box) box.innerHTML = searchResults(this.state);
    } else if (refl) {
      this.scheduleReflectionSave(refl, (t as HTMLTextAreaElement).value);
    }
  }

  // Debounced auto-save for reflection textareas (no full re-render — keeps focus).
  private scheduleReflectionSave(key: string, value: string): void {
    const status = document.getElementById("refl-status-" + key);
    if (status) status.textContent = "Saving…";
    clearTimeout(this.reflTimers[key]);
    this.reflTimers[key] = window.setTimeout(async () => {
      try {
        await saveReflectionApi(key, value);
        this.state.reflections[key] = { body: value, updatedAt: Date.now() };
        const s = document.getElementById("refl-status-" + key);
        if (s) s.textContent = "Saved";
      } catch (err) {
        if (this.isAuthErr(err)) return this.handleAuthErr(err);
        const s = document.getElementById("refl-status-" + key);
        if (s) s.textContent = "Couldn't save — check your connection";
      }
    }, 700);
  }

  private onKeyDown(e: KeyboardEvent): void {
    const t = e.target as HTMLElement;
    if (t.id === "composer" && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void this.send(this.draft);
    }
  }

  private onGlobalKey(e: KeyboardEvent): void {
    const k = (e.key || "").toLowerCase();
    if ((e.metaKey || e.ctrlKey) && k === "k") {
      if (!this.state.authed) return;
      e.preventDefault();
      this.set({ searchOpen: !this.state.searchOpen, query: "" });
    } else if (k === "escape" && this.state.searchOpen) {
      this.set({ searchOpen: false });
    } else if (k === "escape" && this.state.accountMenuOpen) {
      this.set({ accountMenuOpen: false, confirmDelete: false });
    }
  }

  private onClick(e: MouseEvent): void {
    const el = (e.target as HTMLElement).closest("[data-act]") as HTMLElement | null;
    if (!el) return;
    const act = el.getAttribute("data-act")!;
    this.dispatch(act, {
      id: el.getAttribute("data-id") || "",
      ask: el.getAttribute("data-ask") || "",
      name: el.getAttribute("data-name") || "",
      mid: el.getAttribute("data-mid") || "",
      q: el.getAttribute("data-q") || "",
      v: el.getAttribute("data-v") || "",
      theme: el.getAttribute("data-theme") || "",
      status: el.getAttribute("data-status") || "",
    });
  }

  // -------------------------------------------------------------- navigation
  private go(route: Route, extra: Partial<AppState> = {}): void {
    this.set({ route, searchOpen: false, accountMenuOpen: false, confirmDelete: false, navOpen: false, ...extra });
    this.pushPath();
    this.scrollCanvasTop();
    this.onRouteEntered();
  }

  // Lazy-load data the entered route needs.
  private onRouteEntered(): void {
    if (!this.state.authed) return;
    if (this.state.route === "practice" && !this.state.reflectionsLoaded) void this.loadPractice();
    if (this.state.route === "suggestions" && !this.state.suggestionsLoaded) void this.loadSuggestions();
  }

  private isAuthErr(err: unknown): boolean {
    return String((err as Error)?.message).includes("unauthorized");
  }
  private handleAuthErr(err: unknown): void {
    if (this.isAuthErr(err)) this.set({ sending: false, authed: false, authError: "expired" });
  }

  private async loadPractice(): Promise<void> {
    try {
      const [r, d] = await Promise.all([fetchReflections(), fetchDiagnostics()]);
      this.set({ reflections: r.reflections, reflectionsLoaded: true, diagResults: d.results });
    } catch (err) {
      this.handleAuthErr(err);
    }
  }

  private async loadSuggestions(): Promise<void> {
    try {
      const r = await fetchSuggestions();
      this.set({ suggestions: r.suggestions, suggestionsLoaded: true });
    } catch (err) {
      this.handleAuthErr(err);
    }
  }

  private pushPath(): void {
    const path = this.buildPath();
    if (location.pathname !== path) history.pushState({}, "", path);
  }

  private buildPath(): string {
    const s = this.state;
    switch (s.route) {
      case "home":
        return "/";
      case "wiki":
        return s.wikiTab === "frameworks" ? "/wiki" : `/wiki/${s.wikiTab}`;
      case "framework":
        return `/framework/${s.frameworkId}`;
      case "session":
        return `/session/${s.sessionId}`;
      default:
        return `/${s.route}`;
    }
  }

  private applyPath(pathname: string): void {
    const parts = pathname.split("/").filter(Boolean);
    if (parts.length === 0) {
      this.state.route = "home";
      return;
    }
    const [head, sub] = parts;
    if (head === "wiki") {
      this.state.route = "wiki";
      this.state.wikiTab = (["frameworks", "sessions", "lexicon"].includes(sub) ? sub : "frameworks") as WikiTab;
    } else if (head === "framework" && sub && FRAMEWORKS[sub]) {
      this.state.route = "framework";
      this.state.frameworkId = sub;
    } else if (head === "session" && sub) {
      this.state.route = "session";
      this.state.sessionId = sub;
    } else if (["patterns", "practice", "suggestions", "account"].includes(head)) {
      this.state.route = head as Route;
    } else {
      this.state.route = "home";
    }
  }

  // -------------------------------------------------------------- dispatch
  private dispatch(
    act: string,
    args: { id: string; ask: string; name: string; mid: string; q: string; v: string; theme: string; status: string }
  ): void {
    switch (act) {
      case "goHome":
        return this.go("home");
      case "goWiki":
      case "startHere":
        return this.go("wiki", { wikiTab: "frameworks" });
      case "goSessionsTab":
        return this.go("wiki", { wikiTab: "sessions" });
      case "goPatterns":
        return this.go("patterns");
      case "goPractice":
        return this.go("practice");
      case "goSuggestions":
        return this.go("suggestions");
      case "goAccount":
        return this.go("account", { confirmDelete: false });
      case "toggleNav":
        return this.set({ navOpen: !this.state.navOpen });
      case "closeNav":
        return this.set({ navOpen: false });
      case "openFw":
        return this.go("framework", { frameworkId: args.id || "clark" });
      case "openSession":
        return this.go("session", { sessionId: args.id || "reitz" });
      case "setReturning":
        return this.set({ viewState: "returning", route: "home", searchOpen: false });
      case "setFirstRun":
        return this.set({ viewState: "firstRun", route: "home", searchOpen: false });
      case "tabFrameworks":
        return this.go("wiki", { wikiTab: "frameworks" });
      case "tabSessions":
        return this.go("wiki", { wikiTab: "sessions" });
      case "tabLexicon":
        return this.go("wiki", { wikiTab: "lexicon" });
      case "toggleCompanion":
        return this.set({ companionOpen: !this.state.companionOpen });
      case "openCompanion":
        return this.set({ companionOpen: true });
      case "toggleCompanionWidth": {
        const wide = !this.state.companionWide;
        try {
          localStorage.setItem("hive_companion_wide", wide ? "1" : "0");
        } catch {
          /* localStorage unavailable */
        }
        return this.set({ companionWide: wide });
      }
      case "requestClearChat":
        return this.set({ confirmClear: true });
      case "cancelClearChat":
        return this.set({ confirmClear: false });
      case "confirmClearChat":
        return void this.clearChat();
      case "toggleChallenge":
        return this.set({ challengeMode: !this.state.challengeMode });
      case "talkChallenge": {
        const c = this.state.challenge.trim();
        this.draft = c
          ? `Help me with my challenge: ${c}`
          : "Help me raise a structural concern with my VP without it landing as a complaint.";
        return this.set({ companionOpen: true });
      }
      case "askAboutFw": {
        const fw = FRAMEWORKS[args.id];
        this.draft = `How do I use ${fw ? fw.name : "this framework"} at GeoComply?`;
        return this.set({ companionOpen: true });
      }
      case "deepenFw": {
        const fw = FRAMEWORKS[args.id];
        const prompt = fw
          ? `Go deeper on "${fw.name}" using the full programme materials — give me the key examples, anecdotes and any data from the session, and how to apply it in practice.`
          : "Go deeper on this framework using the programme materials.";
        return void this.send(prompt);
      }
      case "deepenSession": {
        const ses = SESSIONS[args.id];
        const prompt = ses
          ? `Tell me more about ${ses.faculty}'s session "${ses.sessionTitle}" from the programme materials — the key points, examples and exercises, in depth.`
          : "Tell me more about this session from the programme materials.";
        return void this.send(prompt);
      }
      case "starterExplain":
        return void this.send("What's the difference between Clark's stage 3 and stage 4?");
      case "starterDiagram":
        return void this.send("Draw the change equation as a diagram.");
      case "starterResolve":
        return void this.send("Help me prepare for a tense conversation with a senior engineer this week.");
      case "askLex":
        this.set({ companionOpen: true });
        return void this.send(args.ask);
      case "reflectPrompt":
        this.set({ companionOpen: true });
        return void this.send(`I'd like to reflect on this question from the residential: "${args.ask}". Ask me one good question to get me started, then help me think it through.`);
      case "practiseMove": {
        const fw = FRAMEWORKS[args.id];
        const where = fw ? ` from ${fw.name}` : "";
        this.set({ companionOpen: true });
        return void this.send(`I want to practise this move${where}: "${args.ask}". Ask me about a real situation where I could try it, then coach me through doing it — give me a concrete way to apply it this week.`);
      }
      case "checkWatchOut": {
        const fw = FRAMEWORKS[args.id];
        const where = fw ? ` with ${fw.name}` : "";
        this.set({ companionOpen: true });
        return void this.send(`Help me check myself against this watch-out${where}: "${args.ask}". Ask me an honest question to see whether I'm falling into it, then suggest how to catch and correct it.`);
      }
      case "sendDraft":
        return void this.send(this.draft);
      case "openCite":
        return this.openCite(args.name);
      case "saveNote":
        return void this.saveNote(args.mid);
      case "toggleAccountMenu":
        return this.set({ accountMenuOpen: !this.state.accountMenuOpen, confirmDelete: false });
      case "closeAccountMenu":
        return this.set({ accountMenuOpen: false, confirmDelete: false });
      case "requestDeleteData":
        return this.set({ confirmDelete: true });
      case "cancelDeleteData":
        return this.set({ confirmDelete: false });
      case "confirmDeleteData":
        return void this.deleteAccount();
      case "openSearch":
        return this.set({ searchOpen: true, query: "" });
      case "closeSearch":
        return this.set({ searchOpen: false });
      case "stop":
        return; // swallow click inside the palette
      case "editChallenge":
        return this.set({ editingChallenge: true });
      case "cancelChallenge":
        return this.set({ editingChallenge: false });
      case "saveChallenge":
        return void this.commitChallenge();
      // ---- practice: labs + diagnostics
      case "practiceDiagnostics":
        return this.set({ practiceTab: "diagnostics" });
      case "practiceLabs":
        return this.set({ practiceTab: "labs", activeLab: null });
      case "startLab":
        this.scrollCanvasTop();
        return this.set({ practiceTab: "labs", activeLab: args.id });
      case "openLab":
        return this.go("practice", { practiceTab: "labs", activeLab: args.id });
      case "exitLab":
        this.scrollCanvasTop();
        return this.set({ activeLab: null });
      case "labTalk":
        return this.labTalk(args.id);
      case "openDiagnostic":
        return this.go("practice", { practiceTab: "diagnostics", activeDiagnostic: args.id, diagAnswers: {}, diagOutcome: null });
      case "startDiagnostic":
      case "retakeDiagnostic":
        return this.set({ practiceTab: "diagnostics", activeDiagnostic: args.id, diagAnswers: {}, diagOutcome: null });
      case "answerDiag":
        return this.set({ diagAnswers: { ...this.state.diagAnswers, [args.q]: parseInt(args.v, 10) } });
      case "submitDiagnostic":
        return this.submitDiagnostic();
      case "exitDiagnostic":
        return this.set({ activeDiagnostic: null, diagOutcome: null });
      case "reflectDiagnostic":
        this.scrollCanvasTop();
        return this.set({ practiceTab: "labs", activeDiagnostic: null, diagOutcome: null });
      case "talkDiagnostic":
        return this.talkDiagnostic(args.id);

      // ---- patterns
      case "patternFilter":
        return this.set({ patternFilter: args.theme || "all" });

      // ---- suggestions
      case "toggleSuggestionAnon":
        return this.set({ suggestionAnon: !this.state.suggestionAnon });
      case "submitSuggestion":
        return void this.submitSuggestion();
      case "voteSuggestion":
        return void this.voteSuggestion(args.id);
      case "setSuggestionStatus":
        return void this.setStatus(args.id, args.status);
      case "sortSuggestionsTop":
        return this.set({ suggestionSort: "top" });
      case "sortSuggestionsNew":
        return this.set({ suggestionSort: "new" });

      case "logout":
        location.href = "/auth/logout";
        return;
      default:
        return;
    }
  }

  // -------------------------------------------------------------- companion
  private citeToFw(name: string): string | null {
    const low = name.toLowerCase();
    for (const id of FRAMEWORK_ORDER) {
      const n = FRAMEWORKS[id].name.toLowerCase();
      if (n.includes(low.slice(0, 12)) || low.includes(n.slice(0, 12))) return id;
    }
    if (low.includes("safety") || low.includes("clark")) return "clark";
    if (low.includes("change") || low.includes("equation")) return "change-equation";
    if (low.includes("speak") || low.includes("advocacy") || low.includes("truth") || low.includes("avoid")) return "truth";
    return null;
  }

  private openCite(name: string): void {
    const id = this.citeToFw(name);
    if (id) this.go("framework", { frameworkId: id });
    else this.go("wiki", { wikiTab: "sessions" });
  }

  // -------------------------------------------------------------- practice
  private submitDiagnostic(): void {
    const d = DIAGNOSTICS.find((x) => x.id === this.state.activeDiagnostic);
    if (!d) return;
    if (d.questions.some((q) => this.state.diagAnswers[q.id] == null)) return; // incomplete
    const res = scoreDiagnostic(d, this.state.diagAnswers);
    this.set({
      diagOutcome: res.outcomeKey,
      diagResults: { ...this.state.diagResults, [d.id]: { outcomeKey: res.outcomeKey, scores: res.dimScores, createdAt: Date.now() } },
    });
    this.scrollCanvasTop();
    saveDiagnosticApi(d.id, res.outcomeKey, res.dimScores).catch((err) => this.handleAuthErr(err));
  }

  private labTalk(id: string): void {
    const lab = LABS.find((l) => l.id === id);
    if (!lab) return void this.send("Help me work through this lab.");
    const answers: string[] = [];
    lab.steps.forEach((step, i) => {
      if (!step.input) return;
      const body = (this.state.reflections[`lab:${id}:${i}`]?.body || "").trim();
      if (body) answers.push(`• ${step.title}: ${body}`);
    });
    if (!answers.length) {
      return void this.send(`I'm starting the "${lab.title}" lab. Walk me through it and help me think it through.`);
    }
    void this.send(
      `I've worked through the "${lab.title}" lab. Here are my answers — give me an honest analysis. Reply in three short Markdown sections with bold headers: **Strengths**, **Gaps**, and **Next step** (one concrete action). Add a small Mermaid diagram only if it genuinely clarifies.\n\n${answers.join("\n")}`
    );
  }

  private talkDiagnostic(id: string): void {
    const d = DIAGNOSTICS.find((x) => x.id === id);
    const res = this.state.diagResults[id];
    const key = this.state.diagOutcome || res?.outcomeKey;
    if (!d || !key) return void this.send("Help me act on my diagnostic result.");
    const outcome = d.outcomes[key];
    const scores = res?.scores || {};
    const scoreStr = Object.keys(scores).length
      ? " My scores were — " + Object.entries(scores).map(([k, v]) => `${k}: ${(v as number).toFixed(1)}/${d.scale.max}`).join(", ") + "."
      : "";
    void this.send(
      `I just did the "${d.title}" diagnostic and came out as "${outcome?.label || key}".${scoreStr} Help me interpret it. Reply in three short Markdown sections with bold headers: **What this says about me**, **The main risk**, and **One next step**.`
    );
  }

  // -------------------------------------------------------------- suggestions
  private async submitSuggestion(): Promise<void> {
    const titleEl = document.getElementById("sg-title") as HTMLInputElement | null;
    const bodyEl = document.getElementById("sg-body") as HTMLTextAreaElement | null;
    const title = (titleEl?.value || "").trim();
    const body = (bodyEl?.value || "").trim();
    if (!title) {
      titleEl?.focus();
      return;
    }
    try {
      await createSuggestionApi(title, body, this.state.suggestionAnon);
      this.state.suggestionAnon = false;
      await this.loadSuggestions();
    } catch (err) {
      this.handleAuthErr(err);
    }
  }

  private async voteSuggestion(id: string): Promise<void> {
    try {
      await voteSuggestionApi(id);
      await this.loadSuggestions();
    } catch (err) {
      this.handleAuthErr(err);
    }
  }

  private async setStatus(id: string, status: string): Promise<void> {
    try {
      await setSuggestionStatusApi(id, status);
      await this.loadSuggestions();
    } catch (err) {
      this.handleAuthErr(err);
    }
  }

  private async send(textRaw: string): Promise<void> {
    const text = (textRaw || "").trim();
    if (!text || this.state.sending) return;

    this.draft = "";
    const messages = this.state.messages.concat([
      { role: "user", shown: text, full: text },
      { role: "assistant", shown: "", full: "", pending: true },
    ]);
    const assistantIndex = messages.length - 1;
    this.set({ messages, sending: true, companionOpen: true });

    try {
      const done = await streamChat(text, this.state.challengeMode, (delta) => {
        const m = this.state.messages[assistantIndex];
        if (!m) return;
        m.shown += delta;
        m.pending = false;
        this.scheduleRender();
      });
      const m = this.state.messages[assistantIndex];
      if (m) {
        m.id = done.messageId;
        m.shown = done.text;
        m.full = done.text;
        m.diagram = done.diagram;
        m.cites = done.cites;
        m.pending = false;
        m.done = true;
      }
      this.set({ sending: false });
    } catch (err) {
      if (String((err as Error)?.message).includes("unauthorized")) {
        // Session timed out — drop cleanly to the sign-in gate.
        this.set({ sending: false, authed: false, authError: "expired" });
        return;
      }
      const m = this.state.messages[assistantIndex];
      if (m) {
        m.shown = "I couldn't reach the Companion just now. Please try again in a moment.";
        m.full = m.shown;
        m.pending = false;
        m.done = false;
      }
      this.set({ sending: false });
    }
  }

  private async saveNote(messageId: string): Promise<void> {
    if (!messageId) return;
    const ok = await saveNoteApi(messageId);
    if (!ok) return;
    const m = this.state.messages.find((x) => x.id === messageId);
    if (m) m.saved = true;
    this.scheduleRender();
  }

  private async clearChat(): Promise<void> {
    this.set({ messages: [], confirmClear: false, sending: false });
    try {
      await clearChatApi();
    } catch (err) {
      this.handleAuthErr(err);
    }
  }

  private async deleteAccount(): Promise<void> {
    try {
      await deleteAccountApi();
    } catch {
      /* even on error, take the user out — the server clears the cookie on success */
    }
    location.href = "/";
  }

  private async commitChallenge(): Promise<void> {
    const ci = document.getElementById("challenge-input") as HTMLTextAreaElement | null;
    const value = (ci?.value || "").trim();
    await saveChallenge(value);
    if (this.state.user) this.state.user.challenge = value;
    this.set({ challenge: value, editingChallenge: false });
  }
}
